import React, { useState, useEffect } from 'react';
import { Box, Button, Container, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography, IconButton, CircularProgress } from '@mui/material';
import { closeBtn, logoMastercard, Visalogo } from '../assets/images';
import Grid from '@mui/material/Grid2';
import AddCardDetails from '../modals/AddCardDetails';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DeleteIcon } from '../assets/icons';
import ChargebeeService from '../services/chargebee.service';
import { Invoice } from '../types/chargebee';
import { decodeMetadataToken } from '../utils/token.utils';
import Loader from './common/Loader';
import { toast } from 'react-toastify';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface PaymentMethod {
  id: string;
  type: string;
  status: string;
  isPrimary: boolean;
  isBackup: boolean;
  card: {
    firstName: string;
    lastName: string;
    brand: string;
    last4: string;
    expiryMonth: number;
    expiryYear: number;
  };
}

interface BillingDetails {
  paymentMethods: PaymentMethod[];
  billingAddress: {
    email: string;
    company: string;
    line1: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
  subscription?: {
    nextBillingAt: number;
    currency: string;
  };
}

const Billing = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [visibleCount, setVisibleCount] = useState(5);
  const [showAll, setShowAll] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [itemsPerPage] = useState(5);
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(null);
  const [billingDetails, setBillingDetails] = useState<BillingDetails | null>(null);
  const [isLoadingBilling, setIsLoadingBilling] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [activeCardIndex, setActiveCardIndex] = useState(0);

  const handleLoadMoreToggle = () => {
    setVisibleCount(prevCount => prevCount + itemsPerPage);
    setShowAll(visibleCount + itemsPerPage >= displayInvoices.length);
  };

  function createInvoiceRow(
    name: string,
    date: string,
    status: string,
    amount: string,
    action?: ''
  ) {
    return { name, date, status, amount, action };
  }

  const [isDownloading, setIsDownloading] = useState<Record<string, boolean>>({});

  const handleDownloadInvoice = async (invoiceId: string) => {
    try {
      console.log("clicked start");
      setIsDownloading(prev => ({ ...prev, [invoiceId]: true }));
      const blob = await ChargebeeService.downloadInvoice(invoiceId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      console.log("clicked end");
      setIsDownloading(prev => ({ ...prev, [invoiceId]: false }));

    } catch (error) {
      console.error('Failed to download invoice:', error);
      setIsDownloading(prev => ({ ...prev, [invoiceId]: false }));
      // TODO: Add proper error handling/notification here
    }
  };

  useEffect(() => {
    const fetchInvoices = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const metadata = decodeMetadataToken();
        const customerId = metadata?.companies?.[0]?.id
        const response = await ChargebeeService.getCustomerInvoices(customerId);
        setInvoices(response.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch invoices');
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, []);

  useEffect(() => {
    const fetchBillingDetails = async () => {
      setIsLoadingBilling(true);
      setBillingError(null);
      try {
        const metadata = decodeMetadataToken();
        const customerId = metadata?.companies?.[0]?.id;
        const response = await ChargebeeService.getBillingDetails(customerId);
        setBillingDetails(response.data);

        // Sort payment methods - primary card first, then backup card
        if (response.data?.paymentMethods?.length > 0) {
          const sortedPaymentMethods = [...response.data.paymentMethods].sort((a, b) => {
            if (a.isPrimary) return -1;
            if (b.isPrimary) return 1;
            if (a.isBackup) return -1;
            if (b.isBackup) return 1;
            return 0;
          });
          
          response.data.paymentMethods = sortedPaymentMethods;
          setBillingDetails(response.data);
        }
      } catch (err) {
        setBillingError(err instanceof Error ? err.message : 'Failed to fetch billing details');
      } finally {
        setIsLoadingBilling(false);
      }
    };

    fetchBillingDetails();
  }, []);

  const displayInvoices = invoices
    .filter(invoice => {
      if (!selectedDate) return true;
      
      const invoiceDate = dayjs(invoice.date * 1000);
      return invoiceDate.month() === selectedDate.month() && 
             invoiceDate.year() === selectedDate.year();
    })
    .map(invoice => createInvoiceRow(
      `#${invoice.id}`,
      new Date(invoice.date * 1000).toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      }),
      invoice.status,
      `${invoice.currency_code} ${(invoice.total / 100).toFixed(2)}`
    ));

  const CustomTabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  };

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  };

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };


  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleConfirm = () => {
    console.log('Confirmed!');
    setDialogOpen(false);
  };

  const currentYear = dayjs()

  const handleUpdateBilling = async () => {
    try {
      await ChargebeeService.openBillingSection();
    } catch (error) {
      console.error('Failed to open billing section:', error);
      toast.error('Failed to update billing details');
    }
  };

  const handleUpdateCard = async () => {
    try {
      // Define callback function to refresh billing details
      const refreshBillingDetails = async () => {
        try {
          const metadata = decodeMetadataToken();
          const customerId = metadata?.companies?.[0]?.id;
          if (customerId) {
            const response = await ChargebeeService.getBillingDetails(customerId);
            setBillingDetails(response.data);
            
            // Sort payment methods - primary card first, then backup card
            if (response.data?.paymentMethods?.length > 0) {
              const sortedPaymentMethods = [...response.data.paymentMethods].sort((a, b) => {
                if (a.isPrimary) return -1;
                if (b.isPrimary) return 1;
                if (a.isBackup) return -1;
                if (b.isBackup) return 1;
                return 0;
              });
              
              response.data.paymentMethods = sortedPaymentMethods;
              setBillingDetails(response.data);
            }
            
            toast.success('Payment method updated successfully');
          }
        } catch (refreshError) {
          console.error('Failed to refresh billing details:', refreshError);
        }
      };

      await ChargebeeService.openPaymentSection(refreshBillingDetails);
      
    } catch (error) {
      console.error('Failed to get payment section:', error);
      toast.error('Failed to update card');
    }
  }

  const handleDateChange = (newDate: dayjs.Dayjs | null) => {
    setSelectedDate(newDate);
    setVisibleCount(itemsPerPage);
    setShowAll(false);
  };

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <TableRow>
          <TableCell colSpan={5} align="center"><Loader fullHeight minHeight='200px' gifSize={50}/></TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={5} align="center" sx={{ color: 'error.main' }}>{error}</TableCell>
        </TableRow>
      );
    }

    if (displayInvoices.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={5} align="center">No invoices available</TableCell>
        </TableRow>
      );
    }

    return displayInvoices.slice(0, visibleCount).map((row) => (
      <TableRow
        key={row.name}
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      >
        <TableCell align="left" component="th" scope="row" sx={{ color: '#231F1F', fontSize: '18px', borderBottom: '1px solid #E8EAF0' }}>
          {row.name}
        </TableCell>
        <TableCell align="left" sx={{ color: '#231F1F', fontSize: '18px', borderBottom: '1px solid #E8EAF0' }}>{row.date}</TableCell>
        <TableCell align="left" sx={{ color: '#231F1F', fontSize: '18px', borderBottom: '1px solid #E8EAF0' }}> 
          <Box display="flex" gap='6px' alignItems='center'>
            <Box sx={{
              backgroundColor: row.status === 'paid' ? '#22B0601A' : row.status === 'due' ? '#FCEDEF' : 'transparent',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '32px',
              minWidth: '71px',
              fontSize: '16px',
              color: row.status === 'paid' ? '#22B060' : row.status === 'due' ? '#E44258' : '#000000',
            }}>
              {row.status.charAt(0).toUpperCase() + row.status.slice(1)}
            </Box> 
            {row.status === 'due' && <Typography display='flex'> (<Typography color='#178EFC' fontSize='16px'>Pay Now </Typography>)</Typography>}
          </Box>
        </TableCell>
        <TableCell align="left" sx={{ color: '#231F1F', fontSize: '18px', borderBottom: '1px solid #E8EAF0' }}>{row.amount}</TableCell>
        <TableCell align="left" sx={{ cursor: 'pointer' }}><Tooltip title="Download" arrow>
          <Box width="28px" height="28px" borderRadius="4px" border="1px solid #E8EAF0" padding="2px" display="flex" alignItems="center" justifyContent="center" sx={{ cursor: 'pointer', ":hover": { borderColor: "#666666" } }} onClick={() => handleDownloadInvoice(row.name.replace('#', ''))}>{isDownloading[row.name.replace('#', '')] ? <CircularProgress size={20} /> : <img src={closeBtn} alt="download-btn" />}</Box>
        </Tooltip></TableCell>
      </TableRow>
    ));
  };

  const renderBillingInfo = () => {
    if (isLoadingBilling) {
      return <Typography>Loading billing details...</Typography>;
    }

    if (billingError) {
      return <Typography color="error">{billingError}</Typography>;
    }

    const paymentMethods = billingDetails?.paymentMethods || [];
    const activePaymentMethod = paymentMethods.length > 0 ? paymentMethods[activeCardIndex] : undefined;
    const address = billingDetails?.billingAddress;
    const metadata = decodeMetadataToken();
    const subscription = metadata?.companies?.[0]?.subscription;
    const nextBillingDate = subscription?.nextBillingAt 
      ? new Date(subscription.nextBillingAt * 1000).toLocaleDateString('en-US', { 
          month: 'long', 
          day: 'numeric', 
          year: 'numeric' 
        })
      : '-- --';

    const hasMultipleCards = paymentMethods.length > 1;

    return (
      <Grid container rowSpacing={{ xs: 3, sm: 3, md: 3 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
          <Box boxShadow='0px 4px 12px 0px #2B2B300F' padding='26px' border='1px solid #E8EAF0' bgcolor="#ffffff" display='flex' borderRadius='8px' flexWrap={{ xs: 'wrap', xl: 'unset' }} justifyContent={{ xs: 'flex-end', xl: 'space-between' }} position="relative" flexDirection="column">
            {activePaymentMethod && (
              <Box position="absolute" top={15} right={15}>
                {activePaymentMethod.isPrimary && (
                  <Box 
                    sx={{
                      backgroundColor: '#F0F7FF',
                      color: '#178EFC',
                      borderRadius: '4px',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Primary
                  </Box>
                )}
                {activePaymentMethod.isBackup && (
                  <Box 
                    sx={{
                      backgroundColor: '#F0F7FF',
                      color: '#178EFC',
                      borderRadius: '4px',
                      px: 1.5,
                      py: 0.5,
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Backup
                  </Box>
                )}
              </Box>
            )}
            <Box width="100%">
              <Box mb="18px">
                <Typography color='#231F1F' fontSize='20px' fontWeight='500'>
                  Payment Method
                </Typography>
              </Box>
               
              {activePaymentMethod ? (
                <>
                  <Typography color='#666666' fontSize='16px' mb='8px'>Card Information</Typography>
                  <Box display='flex'>
                    <Typography color='#231F1F' fontSize='16px' mb='8px' mr='20px'>
                      {(activePaymentMethod.card.brand.charAt(0).toUpperCase() + activePaymentMethod.card.brand.slice(1))} ending in {activePaymentMethod.card.last4}
                    </Typography>
                    <Box border='1px solid #E8EAF0' padding='5px 20px' borderRadius='4px' width='70px' display='flex' justifyContent='center' alignItems='center' minWidth='70px'>
                      <img src={activePaymentMethod.card.brand.toLowerCase() === 'visa' ? Visalogo : logoMastercard} alt="" />
                    </Box>
                  </Box>
                  <Box>
                    <Typography color='#666666' fontSize='16px' mb='8px'>Name on Card</Typography>
                    <Typography color='#231F1F' fontSize='16px'>
                    {activePaymentMethod?.card?.firstName || '--'} {activePaymentMethod?.card?.lastName || '--'}
                    </Typography>
                  </Box>
                </>
              ) : (
                <Typography color='#666666'>No payment method available</Typography>
              )}
            </Box>
            
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={0} width="100%">
              <Box width="80px">
                {/* Empty box with fixed width to balance layout */}
              </Box>
              
              <Box>
                {hasMultipleCards && (
                  <Box display="flex" gap={1} justifyContent="center">
                    {paymentMethods.map((_, index) => (
                      <Box 
                        key={index}
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: index === activeCardIndex ? '#178EFC' : '#D9D9D9',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => setActiveCardIndex(index)}
                      />
                    ))}
                  </Box>
                )}
              </Box>
              
              <Button
                variant='outlined'
                size='large'
                sx={{ textTransform: 'capitalize', borderRadius: '8px' }}
                onClick={handleUpdateCard}
              >
                Update
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
          <Box boxShadow='0px 4px 12px 0px #2B2B300F' padding='26px' border='1px solid #E8EAF0' display='flex' bgcolor="#ffffff" borderRadius='8px' flexWrap={{ xs: 'wrap', xl: 'unset' }} justifyContent={{ xs: 'flex-end', xl: 'space-between' }}>
            <Box width={{ xs: '100%', xl: 'unset' }}>
              <Box>
                <Typography color='#231F1F' fontSize='20px' fontWeight='500' mb='18px'>Billing Details</Typography>
              </Box>
              <Typography color='#666666' fontSize='16px' mb='8px'>Next Billing Date</Typography>
              <Typography color='#231F1F' fontSize='16px' mb='8px'>{nextBillingDate}</Typography>
              <Box>
                <Typography color='#666666' fontSize='16px' mb='8px'>Billing Address</Typography>
                <Typography color='#231F1F' fontSize='16px'>
                  {address?.line1 ? `${address.line1}, ${address.city}, ${address.state} ${address.zip}, ${address.country}` : 'No billing address available'}
                </Typography>
              </Box>
            </Box>
            <Box>
              <Button
                variant='outlined'
                size='large'
                sx={{ textTransform: 'capitalize', ml: '8px', borderRadius: '8px' }}
                onClick={handleUpdateBilling}
              >
                Update
              </Button>
            </Box>
          </Box>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
          <Box boxShadow='0px 4px 12px 0px #2B2B300F' padding='26px' border='1px solid #E8EAF0' bgcolor="#ffffff" display='flex' justifyContent='space-between' borderRadius='8px'>
            <Box display='flex' justifyContent='center' alignItems='center' width='100%' flexDirection='column'>
              <Box display='flex' gap='40px' mb='40px'>
                <Box border='1px solid #E8EAF0' padding='5px 20px' borderRadius='4px' width='70px' display='flex' justifyContent='center' alignItems='center' minWidth='70px'>
                  <img src={logoMastercard} alt="" />
                </Box>
                <Box border='1px solid #E8EAF0' padding='5px 20px' borderRadius='4px' width='70px' display='flex' justifyContent='center' alignItems='center' minWidth='70px'>
                  <img src={Visalogo} alt="" />
                </Box>
              </Box>

              <Box>
                <Typography color='#000000' fontWeight='500' fontSize={{ xs: '20px', md: '24px' }} mb='21px' maxWidth='295px' margin='auto' textAlign='center' pb='21px'>Add your secondary credit card for payment</Typography>
                <Typography color='#333333' fontSize='16px' maxWidth='424px' margin='auto' textAlign='center'>This credit card will be used as secondary payment for billing</Typography>
              </Box>
              
              <Box mt='32px'>
                <Button variant='contained' onClick={handleUpdateCard} sx={{
                  textTransform: 'capitalize', bgcolor: '#178EFC', color: '#FFFFFF !important', px: '24px', height: '40px', borderRadius: '8px', '&:hover': {
                    border: 'none !important'
                  },
                }}>Add new card</Button>

                <AddCardDetails
                  open={dialogOpen}
                  onClose={handleDialogClose}
                  title="Dialog Title"
                  content="This is the dialog content."
                  onConfirm={handleConfirm}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <Container sx={{ maxWidth: "1200px", px: "0 !Important" }}>
      <Box>
        <Box mb={{ xs: "34px", sm: '20px', xl: "34px" }}>
          <Typography color="#333333" fontSize="24px" mb="15px" fontWeight="500">
            Billing
          </Typography>
          <Typography>Manage Billing Information</Typography>
        </Box>

        <Box sx={{ width: '100%' }} className='settings-module-listing'>
          <Box sx={{ borderColor: 'divider' }} display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap="12px">
            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
              <Tab label="Invoices" {...a11yProps(0)} sx={{ textTransform: 'capitalize' }} />
              <Tab label="Billing Info" {...a11yProps(1)} sx={{ textTransform: 'capitalize' }} />
            </Tabs>
            {value === 0 && (
              <Box display="flex" gap="12px" alignItems="center">
                {selectedDate && (
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => handleDateChange(null)}
                    sx={{
                      textTransform: 'capitalize',
                      border: '1px solid #178EFC',
                      borderRadius: '4px',
                      color: '#178EFC',
                      "&:hover": {
                        border: "1px solid #178EFC",
                      }
                    }}
                  >
                    Clear Filter
                  </Button>
                )}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    maxDate={currentYear}
                    openTo="month"
                    views={['year', 'month']}
                    yearsOrder="desc"
                    className='date-picker-custom'
                    value={selectedDate}
                    onChange={handleDateChange}
                    sx={{
                      backgroundColor: "#ffffff",
                      '& .MuiInputBase-root': {
                        cursor: 'pointer',
                        height: '40px',
                      },
                      '& .MuiInputBase-input': {
                        cursor: 'pointer',
                      }
                    }}
                    slotProps={{
                      textField: {
                        placeholder: 'Filter by Month',
                        inputProps: {
                          readOnly: true,
                          style: { cursor: 'pointer' }
                        },
                        onClick: (e) => e.currentTarget.querySelector('button')?.click()
                      },
                      layout: {
                        sx: {
                          minWidth: 246,
                          '.MuiPickersMonth-monthButton.Mui-selected, .MuiPickersMonth-monthButton:hover': {
                            color: '#ffffff',
                            backgroundColor: '#1272CA',
                          },
                          '.MuiPickersYear-yearButton.Mui-selected, .MuiPickersYear-YearButton:hover': {
                            color: '#ffffff',
                            backgroundColor: '#1272CA !important',
                          },
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Box>
            )}
          </Box>
          <CustomTabPanel value={value} index={0}>
            <TableContainer component={Paper} sx={{ boxShadow: '0px 4px 12px 0px #2B2B300F' }}>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Invoice Number</TableCell>
                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Invoice Date</TableCell>
                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', width: '20%', fontWeight: "400" }}>Status</TableCell>
                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Amount</TableCell>
                    <TableCell align="left" sx={{ textTransform: 'capitalize', color: '#666666', fontSize: '16px', borderBottom: '1px solid #E8EAF0', fontWeight: "400" }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderTableContent()}
                </TableBody>
              </Table>
            </TableContainer>
            {displayInvoices.length > itemsPerPage && (
              <Box display='flex' justifyContent="center" mt="24px">
                <Button
                  variant='outlined'
                  size='large'
                  sx={{
                    textTransform: 'capitalize',
                    border: '1px solid #178EFC',
                    borderRadius: '4px',
                    color: '#178EFC',
                    "&:hover": {
                      border: "1px solid #178EFC",
                    }
                  }}
                  onClick={handleLoadMoreToggle}
                  disabled={visibleCount >= displayInvoices.length}
                >
                  Load More
                </Button>
              </Box>
            )}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            {renderBillingInfo()}
          </CustomTabPanel>
        </Box>
      </Box >
    </Container>
  );
};

export default Billing;
